import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Loader2, Check, Car, Bus, HandHeart, CreditCard, GraduationCap, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ANNUAL_KEYS = ["general_participation", "emergency_medical", "photo_media"];

export default function RegistrationDialog({ open, onOpenChange, exp }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [membership, setMembership] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [responses, setResponses] = useState([]);
  const [selected, setSelected] = useState([]);
  const [transport, setTransport] = useState("parent");
  const [chaperone, setChaperone] = useState(false);
  const [method, setMethod] = useState("card");
  const [loading, setLoading] = useState(false);
  const [signForm, setSignForm] = useState(null);
  const [sig, setSig] = useState("");
  const [sigAck, setSigAck] = useState(false);
  const [mediaChoice, setMediaChoice] = useState("authorize");

  const loadForms = useCallback(() => {
    api.get("/form-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/form-responses/me").then((r) => setResponses(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (open && user) {
      api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
      api.get("/membership/me").then((r) => setMembership(r.data.active ? r.data : null)).catch(() => {});
      loadForms();
    }
  }, [open, user, loadForms]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const isMember = !!membership;
  const perStudent = exp.credit_eligible && isMember ? 0 : (isMember ? exp.member_price : exp.non_member_price);
  const transportCost = transport === "hea" && exp.transport_enabled ? exp.transport_price : 0;
  const estTotal = selected.length * perStudent + selected.length * transportCost;

  // required forms
  const eventKeys = ["field_trip", ...(transport === "hea" ? ["transportation"] : [])];
  const requiredKeys = [...ANNUAL_KEYS, ...eventKeys];
  const isSigned = (key) => {
    if (ANNUAL_KEYS.includes(key)) return responses.some((r) => r.template_key === key && !r.experience_id);
    return responses.some((r) => r.template_key === key && r.experience_id === exp.id);
  };
  const missing = templates.filter((t) => requiredKeys.includes(t.key) && !isSigned(t.key));

  const doSign = async () => {
    if (!sig.trim() || !sigAck) { toast.error("Type your name and confirm your intent to sign."); return; }
    try {
      await api.post("/form-responses", {
        template_id: signForm.id, signature: sig, acknowledged: sigAck,
        student_ids: signForm.category === "event" ? selected : [],
        experience_id: signForm.category === "event" ? exp.id : null,
        media_choice: signForm.key === "photo_media" ? mediaChoice : null,
      });
      toast.success(`${signForm.name} signed.`);
      setSignForm(null); setSig(""); setSigAck(false); setMediaChoice("authorize");
      const r = await api.get("/form-responses/me"); setResponses(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submit = async () => {
    if (selected.length === 0) { toast.error("Select at least one student."); return; }
    if (missing.length > 0) { toast.error("Please complete all required forms first."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/registrations", {
        experience_id: exp.id, student_ids: selected, transport_option: transport,
        chaperone_interest: chaperone, payment_method: method,
        payment_type: method === "sufs" ? "SCHOLARSHIP" : "PRIVATE_PAY",
        origin_url: window.location.origin,
      });
      if (data.sufs) {
        toast.success("Registration submitted — awaiting scholarship verification.");
        onOpenChange(false); navigate("/dashboard");
      } else if (data.waitlisted) {
        toast.success("Added to the waitlist. We'll notify you if a spot opens.");
        onOpenChange(false); navigate("/dashboard");
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.success("Registration confirmed!");
        onOpenChange(false); navigate("/dashboard");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (!user) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Sign in to register</DialogTitle></DialogHeader>
          <p className="text-sm text-slateBrand">Create a free family account or log in to register your students for this experience.</p>
          <div className="mt-4 flex gap-3">
            <Button className="flex-1 bg-brandblue" data-testid="reg-login-btn" onClick={() => navigate("/login")}>Log In</Button>
            <Button variant="outline" className="flex-1" data-testid="reg-register-btn" onClick={() => navigate("/register")}>Create Account</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const transportOpts = [
    { v: "parent", label: "Parent Transportation", desc: "I'll drive my own children.", icon: Car },
    ...(exp.transport_enabled ? [{ v: "hea", label: "H.E.A. Transportation", desc: `$${exp.transport_price} per student, round trip.`, icon: Bus }] : []),
    { v: "chaperone", label: "Parent Chaperone", desc: "I'll attend and help chaperone.", icon: HandHeart },
  ];

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl text-navy">Register — {exp.title}</DialogTitle></DialogHeader>

        <div className="space-y-6">
          <div>
            <Label className="font-display font-semibold text-navy">Select students</Label>
            {students.length === 0 ? (
              <div className="mt-2 rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slateBrand">
                No students yet. <button className="font-semibold text-brandblue" onClick={() => navigate("/students")}>Add a child →</button>
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                {students.map((s) => (
                  <label key={s.id} data-testid={`reg-student-${s.id}`}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${selected.includes(s.id) ? "border-brandblue bg-brandblue/5" : "border-slate-200"}`}>
                    <Checkbox checked={selected.includes(s.id)} onCheckedChange={() => toggle(s.id)} />
                    <div>
                      <div className="font-display font-semibold text-navy">{s.first_name} {s.last_name}</div>
                      <div className="text-xs text-slateBrand">Grade {s.grade} {s.age ? `• Age ${s.age}` : ""}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="font-display font-semibold text-navy">Transportation</Label>
            <RadioGroup value={transport} onValueChange={setTransport} className="mt-3 space-y-2">
              {transportOpts.map((o) => (
                <label key={o.v} data-testid={`reg-transport-${o.v}`}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${transport === o.v ? "border-brandblue bg-brandblue/5" : "border-slate-200"}`}>
                  <RadioGroupItem value={o.v} className="mt-1" />
                  <div>
                    <div className="flex items-center gap-2 font-display font-semibold text-navy"><o.icon className="h-4 w-4" />{o.label}</div>
                    <div className="text-xs text-slateBrand">{o.desc}</div>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          <label className="flex items-center gap-3 rounded-lg border border-slate-200 p-3" data-testid="reg-chaperone-check">
            <Checkbox checked={chaperone} onCheckedChange={setChaperone} />
            <span className="text-sm text-navy">I'm interested in volunteering as a chaperone.</span>
          </label>

          {/* REQUIRED FORMS */}
          <div>
            <Label className="font-display font-semibold text-navy">Required forms</Label>
            {selected.length === 0 ? (
              <p className="mt-2 text-xs text-slateBrand">Select students to see required forms.</p>
            ) : missing.length === 0 ? (
              <div className="mt-2 flex items-center gap-2 rounded-lg bg-successBrand/10 p-3 text-sm text-successBrand"><Check className="h-4 w-4" />All required forms are complete.</div>
            ) : (
              <div className="mt-3 space-y-2">
                {missing.map((t) => (
                  <div key={t.id} data-testid={`reg-form-${t.key}`} className="flex items-center justify-between rounded-lg border border-alertBrand/30 bg-alertBrand/5 p-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-navy"><FileText className="h-4 w-4 text-brandblue" />{t.name}</div>
                    <Button size="sm" data-testid={`reg-sign-${t.key}`} onClick={() => { setSignForm(t); setSig(""); setSigAck(false); setMediaChoice("authorize"); }} className="bg-brandblue font-display text-xs font-semibold hover:bg-blue-700">Review & Sign</Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* PAYMENT METHOD */}
          <div>
            <Label className="font-display font-semibold text-navy">Payment method</Label>
            <RadioGroup value={method} onValueChange={setMethod} className="mt-3 space-y-2">
              <label data-testid="reg-method-card" className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${method === "card" ? "border-brandblue bg-brandblue/5" : "border-slate-200"}`}>
                <RadioGroupItem value="card" className="mt-1" />
                <div><div className="flex items-center gap-2 font-display font-semibold text-navy"><CreditCard className="h-4 w-4" />Credit / Debit Card</div><div className="text-xs text-slateBrand">Pay securely now via card.</div></div>
              </label>
              <label data-testid="reg-method-sufs" className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${method === "sufs" ? "border-brandblue bg-brandblue/5" : "border-slate-200"}`}>
                <RadioGroupItem value="sufs" className="mt-1" />
                <div><div className="flex items-center gap-2 font-display font-semibold text-navy"><GraduationCap className="h-4 w-4" />Step Up For Students (SUFS)</div><div className="text-xs text-slateBrand">No card payment now — reserve eligible scholarship funds in EMA, then H.E.A. verifies.</div></div>
              </label>
            </RadioGroup>
            {method === "sufs" && (
              <div className="mt-3 rounded-lg bg-gold/10 p-3 text-xs text-[#8A5F00]">
                Selecting Step Up does not charge funds or confirm your place. After submitting, follow the <button className="font-semibold underline" onClick={() => navigate("/step-up-booking")}>Step Up Booking Guide</button> to reserve funds, then mark it in your dashboard. H.E.A. verifies the reservation and confirms availability.
              </div>
            )}
          </div>

          {/* SUMMARY */}
          <div className="rounded-xl bg-cloud p-4">
            <div className="flex items-center justify-between text-sm text-slateBrand">
              <span>{selected.length} student(s) × ${perStudent} {isMember && "(member)"}</span>
              <span className="font-semibold text-navy">${selected.length * perStudent}</span>
            </div>
            {transportCost > 0 && (
              <div className="mt-1 flex items-center justify-between text-sm text-slateBrand">
                <span>Transportation × {selected.length}</span>
                <span className="font-semibold text-navy">${selected.length * transportCost}</span>
              </div>
            )}
            {method === "sufs" ? (
              <>
                <div className="mt-2 flex items-center justify-between text-sm text-slateBrand">
                  <span>Service price</span>
                  <span className="font-semibold text-navy" data-testid="reg-service-price">${estTotal}</span>
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-3 font-display text-lg font-bold text-navy">
                  <span>Total Due Today</span><span data-testid="reg-total">$0.00</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-sm font-semibold text-[#8A5F00]">
                  <span>Scholarship Amount Pending Verification</span>
                  <span data-testid="reg-scholarship-pending">${estTotal}</span>
                </div>
              </>
            ) : (
              <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-3 font-display text-lg font-bold text-navy">
                <span>Total</span><span data-testid="reg-total">${estTotal}</span>
              </div>
            )}
          </div>

          <Button data-testid="reg-submit-btn" disabled={loading || selected.length === 0 || missing.length > 0} onClick={submit}
            className="h-12 w-full bg-brandblue font-display text-base font-semibold hover:bg-blue-700">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : method === "sufs" ? "Submit Scholarship Registration" : estTotal > 0 ? "Continue to Payment" : <><Check className="mr-2 h-5 w-5" />Confirm Registration</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* SIGN FORM MODAL */}
    <Dialog open={!!signForm} onOpenChange={(o) => !o && setSignForm(null)}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-xl text-navy">{signForm?.name}</DialogTitle></DialogHeader>
        {signForm?.pdf_url && (
          <a href={signForm.pdf_url} target="_blank" rel="noopener noreferrer" data-testid="reg-pdf-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-brandblue hover:underline">
            <FileText className="h-4 w-4" />Open / download the original PDF
          </a>
        )}
        <div className="max-h-56 overflow-y-auto whitespace-pre-line rounded-lg bg-cloud p-4 text-sm leading-relaxed text-slateBrand">{signForm?.body}</div>
        <div className="space-y-4">
          {signForm?.key === "photo_media" && (
            <RadioGroup value={mediaChoice} onValueChange={setMediaChoice} className="space-y-2">
              <label className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm text-navy" data-testid="reg-media-authorize"><RadioGroupItem value="authorize" />I AUTHORIZE photo / media use</label>
              <label className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm text-navy" data-testid="reg-media-decline"><RadioGroupItem value="decline" />I DO NOT AUTHORIZE photo / media use</label>
            </RadioGroup>
          )}
          <div>
            <Label className="font-display font-semibold text-navy">Type your full legal name to sign</Label>
            <Input className="mt-1.5 h-11" data-testid="reg-sign-name" value={sig} onChange={(e) => setSig(e.target.value)} placeholder="Your full name" />
          </div>
          <label className="flex items-start gap-3 text-sm text-navy" data-testid="reg-sign-ack">
            <Checkbox checked={sigAck} onCheckedChange={setSigAck} className="mt-0.5" />
            <span>I intend this to be my electronic signature with the same force as my handwritten signature, and I certify I am the parent/legal guardian.</span>
          </label>
          <Button data-testid="reg-sign-submit" onClick={doSign} className="h-12 w-full bg-brandblue font-display font-semibold hover:bg-blue-700"><Check className="mr-2 h-4 w-4" />Sign Electronically</Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
